import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Product } from '../types'
import { AppliedCoupon } from '../types/coupon'

export interface CartItem {
  product: Product
  quantity: number
}

interface CartState {
  items: CartItem[]
  userId: string | null
  isOpen: boolean // Cart Drawer açık/kapalı durumu
  appliedCoupon: AppliedCoupon | null // Kupon sistemi
  addItem: (product: Product, quantity?: number) => void
  removeItem: (productId: string) => void
  updateQuantity: (productId: string, quantity: number) => void
  clearCart: () => void
  getTotalPrice: () => number
  getTotalItems: () => number
  getItemQuantity: (productId: string) => number
  toggleCart: () => void // Cart Drawer toggle
  
  // Kupon yönetimi
  applyCoupon: (coupon: AppliedCoupon) => void
  removeCoupon: () => void
  getDiscountAmount: () => number
  getFinalTotal: () => number
  
  // Popup yönetimi
  popup: {
    isOpen: boolean
    product: Product | null
    quantity: number
  }
  showPopup: (product: Product, quantity: number) => void
  hidePopup: () => void
  
  // Kullanıcı bazlı sepet yönetimi
  setUserId: (userId: string | null) => void
  loadUserCart: (userId: string) => void
  saveUserCart: (userId: string) => void
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      userId: null,
      isOpen: false,
      appliedCoupon: null,
      popup: {
        isOpen: false,
        product: null,
        quantity: 0
      },

      addItem: (product: Product, quantity = 1) => {
        set((state) => {
          const existingItem = state.items.find(item => item.product.id === product.id)
          let newItems

          if (existingItem) {
            // Ürün zaten sepette varsa, miktarını artır
            newItems = state.items.map(item =>
              item.product.id === product.id
                ? { ...item, quantity: item.quantity + quantity }
                : item
            )
          } else {
            // Yeni ürün ekle
            newItems = [...state.items, { product, quantity }]
          }

          // Giriş yapmış kullanıcı için sepeti kaydet
          if (state.userId) {
            setTimeout(() => get().saveUserCart(state.userId!), 100)
          }

          return { 
            items: newItems,
            popup: { isOpen: true, product, quantity }
          }
        })
      },

      removeItem: (productId: string) => {
        set((state) => {
          const newItems = state.items.filter(item => item.product.id !== productId)
          
          // Giriş yapmış kullanıcı için sepeti kaydet
          if (state.userId) {
            setTimeout(() => get().saveUserCart(state.userId!), 100)
          }

          return { items: newItems }
        })
      },

      updateQuantity: (productId: string, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(productId)
          return
        }

        set((state) => {
          const newItems = state.items.map(item =>
            item.product.id === productId
              ? { ...item, quantity }
              : item
          )

          // Giriş yapmış kullanıcı için sepeti kaydet
          if (state.userId) {
            setTimeout(() => get().saveUserCart(state.userId!), 100)
          }

          return { items: newItems }
        })
      },

      clearCart: () => {
        const { userId } = get()
        set({ items: [], appliedCoupon: null })
        
        // Kullanıcı giriş yapmışsa localStorage'dan da sepeti sil
        if (userId) {
          try {
            localStorage.removeItem(`petunya-cart-${userId}`)
            console.log('✅ Kullanıcı sepeti localStorage\'dan temizlendi:', userId)
          } catch (error) {
            console.error('❌ Kullanıcı sepeti localStorage temizleme hatası:', error)
          }
        }
      },

      getTotalPrice: () => {
        const { items } = get()
        return items.reduce((total, item) => total + (item.product.price * item.quantity), 0)
      },

      getTotalItems: () => {
        const { items } = get()
        return items.reduce((total, item) => total + item.quantity, 0)
      },

      getItemQuantity: (productId: string) => {
        const { items } = get()
        const item = items.find(item => item.product.id === productId)
        return item ? item.quantity : 0
      },

      // Kupon yönetimi
      applyCoupon: (coupon: AppliedCoupon) => {
        set({ appliedCoupon: coupon })
      },

      removeCoupon: () => {
        set({ appliedCoupon: null })
      },

      getDiscountAmount: () => {
        const { appliedCoupon } = get()
        return appliedCoupon ? appliedCoupon.discountAmount : 0
      },

      getFinalTotal: () => {
        const totalPrice = get().getTotalPrice()
        const discountAmount = get().getDiscountAmount()
        return Math.max(0, totalPrice - discountAmount)
      },

      // Kullanıcı ID'sini ayarla
      setUserId: (userId: string | null) => {
        set({ userId })
      },

      // Kullanıcının sepetini yükle
      loadUserCart: (userId: string) => {
        try {
          const savedCart = localStorage.getItem(`petunya-cart-${userId}`)
          if (savedCart) {
            const cartData = JSON.parse(savedCart)
            set({ items: cartData.items || [], userId })
          } else {
            set({ items: [], userId })
          }
        } catch (error) {
          console.error('Kullanıcı sepeti yükleme hatası:', error)
          set({ items: [], userId })
        }
      },

      // Kullanıcının sepetini kaydet
      saveUserCart: (userId: string) => {
        try {
          const { items } = get()
          const cartData = { items, timestamp: Date.now() }
          localStorage.setItem(`petunya-cart-${userId}`, JSON.stringify(cartData))
        } catch (error) {
          console.error('Kullanıcı sepeti kaydetme hatası:', error)
        }
      },
      
      // Cart Drawer yönetimi
      toggleCart: () => 
        set((state) => ({ isOpen: !state.isOpen })),

      // Popup yönetimi
      showPopup: (product: Product, quantity: number) => 
        set({ popup: { isOpen: true, product, quantity } }),
      
      hidePopup: () => 
        set({ popup: { isOpen: false, product: null, quantity: 0 } })
    }),
    {
      name: 'petunya-cart-guest', // Misafir kullanıcılar için
      partialize: (state) => ({ 
        items: state.userId ? [] : state.items // Sadece giriş yapmamış kullanıcılar için persist et
      }),
    }
  )
)